/**
 * Glass Object
 * @author David <david.prenninger@gmx.de>
 *
 * This file provides all helper functions related to Product visualization.
 * It calculates placement and model visualization off all items in objectSections.
 */
import * as THREE from 'three';
import { degreeToMatrix, degreeToMatrixZ } from './geometry';

const placeClamp = (
  origin,
  generatedObject,
  objectDirectionSpace,
  dimensions,
  glassLineRotation,
) => {
  objectDirectionSpace.applyMatrix4(glassLineRotation);

  generatedObject.scale.set(-0.002, -0.002, 0.002);
  generatedObject.castShadow = true;
  generatedObject.receiveShadow = true;
  generatedObject.position.set(
    origin.x + objectDirectionSpace.x,
    dimensions[0].z
      + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0)
      + objectDirectionSpace.y,
    origin.z + objectDirectionSpace.z,
  );
  generatedObject.matrix.multiply(glassLineRotation);
  generatedObject.rotation.setFromRotationMatrix(generatedObject.matrix);
  return generatedObject;
};

const renderClamp = (clamp, origin, glassLineRotation, models, dimensions, first) => {
  if (clamp.model) {
    const material = new THREE.MeshPhongMaterial({
      color: 0xcccccc,
      emissive: 0x0f0f0f,
    });
    const outGroup = new THREE.Object3D();

    // A
    let objectDirectionSpace;
    if (first) {
      objectDirectionSpace = new THREE.Vector3(0, 0.065, -0.045);
    } else {
      objectDirectionSpace = new THREE.Vector3(0, 0.065, 0.03);
    }
    const generatedObject = new THREE.Mesh(models[clamp.id].clone(), material);
    placeClamp(
      origin,
      generatedObject,
      objectDirectionSpace,
      dimensions,
      glassLineRotation,
    );
    outGroup.add(generatedObject);

    let objectDirectionSpaceB;
    if (first) {
      objectDirectionSpaceB = new THREE.Vector3(
        -clamp.length + clamp.distanceBack,
        -0.03,
        -0.045,
      );
    } else {
      objectDirectionSpaceB = new THREE.Vector3(-clamp.length + clamp.distanceBack, -0.03, 0.03);
    }

    const generatedObjectB = new THREE.Mesh(models[`${clamp.id}_B`].clone(), material);

    placeClamp(
      origin,
      generatedObjectB,
      objectDirectionSpaceB,
      dimensions,
      glassLineRotation,
    );

    outGroup.add(generatedObjectB);

    let objectDirectionSpaceC;
    if (first) {
      objectDirectionSpaceC = new THREE.Vector3(
        clamp.length - clamp.distanceFront,
        -0.03,
        -0.045,
      );
    } else {
      objectDirectionSpaceC = new THREE.Vector3(clamp.length - clamp.distanceFront, -0.03, 0.03);
    }

    const generatedObjectC = new THREE.Mesh(models[`${clamp.id}_B`].clone(), material);
    generatedObjectC.geometry.applyMatrix4(degreeToMatrix(180));

    placeClamp(
      origin,
      generatedObjectC,
      objectDirectionSpaceC,
      dimensions,
      glassLineRotation,
    );

    outGroup.add(generatedObjectC);

    return outGroup;
  }
  return null;
};


export default {
  generate: (dimensions, objectSections, offset, models) => {
    const group = new THREE.Object3D();
    const origin = new THREE.Vector3(
      offset.x,
      dimensions[0].z + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0),
      offset.y,
    );

    const glassLineRotation = degreeToMatrix(0);

    objectSections.forEach((section) => {
      if (section.stand) {
        const { stand } = section;
        const material = new THREE.MeshLambertMaterial({
          color: 0x87C3FF,
          reflectivity: 0.8,
          opacity: 0.6,
          transparent: true,
        });
        let generatedObject;

        // Debug Arrows
        // const helper21 = new THREE.ArrowHelper(
        //   THREE.Vector3(1, 0, 0),
        //   origin.clone(),
        //   1,
        //   0x00a2ff,
        // );
        // group.add(helper21);
        // const helper2 = new THREE.ArrowHelper(
        //   THREE.Vector3(0, 1, 0),
        //   origin.clone(),
        //   1,
        //   0x00a2ff,
        // );
        // group.add(helper2);

        const objectDirectionSpace = new THREE.Vector3(0, 0, 0.002);
        objectDirectionSpace.applyMatrix4(glassLineRotation);

        if (stand.model) {
          generatedObject = new THREE.Mesh(models[stand.id].clone(), material);
          generatedObject.scale.set(-0.002, -0.002, 0.002);
          generatedObject.castShadow = true;
          generatedObject.receiveShadow = true;
          generatedObject.position.set(
            origin.x + objectDirectionSpace.x,
            dimensions[0].z + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0),
            origin.z + objectDirectionSpace.z,
          );
          // if (index === 0) {
          //   origin.add(new THREE.Vector3(0.005, 0, 0));
          // }
        } else {
          const objectLength = new THREE.Vector3(0, 0, -0.1);
          const geometry = new THREE.BoxGeometry(stand.length, 0.4, stand.width);
          generatedObject = new THREE.Mesh(geometry, material);
          generatedObject.position.set(
            origin.x + (objectLength.x / 2),
            dimensions[0].z + 0.2 + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0),
            origin.z + (objectLength.z / 2),
          );
        }

        generatedObject.matrix.multiply(glassLineRotation);
        generatedObject.rotation.setFromRotationMatrix(generatedObject.matrix);

        group.add(generatedObject);
      }

      if (section.clamp1) {
        group.add(renderClamp(
          section.clamp1,
          origin,
          glassLineRotation,
          models,
          dimensions,
          true,
        ));
      }

      if (section.connector) {
        const { connector } = section;
        if (connector.model) {
          const material = new THREE.MeshPhongMaterial({
            color: 0xcccccc,
            emissive: 0x0f0f0f,
          });

          const mesh = new THREE.Mesh(models[connector.id].clone(), material);
          mesh.matrix = glassLineRotation;

          let objectLength;
          let objectSize;
          if (connector.angle === 90) {
            objectLength = new THREE.Vector3(connector.length, 0, connector.width);
            // Angle needs to be sized twice
            objectSize = new THREE.Vector3(0, 0, connector.size / 1000);
            if (!section.stand) {
              objectSize = objectSize.clone()
                .applyMatrix4(glassLineRotation)
                .add(objectSize.clone().applyMatrix4(
                  glassLineRotation.clone().multiply(degreeToMatrix(connector.angle)),
                ));
            } else {
              objectSize = objectSize.applyMatrix4(glassLineRotation);
            }
            mesh.rotation.setFromRotationMatrix(mesh.matrix.clone().multiply(degreeToMatrix(180)));
          } else if (connector.isInline) {
            objectLength = new THREE.Vector3(connector.length, 0, connector.width);
            objectSize = new THREE.Vector3(0, 0, connector.size / 500);
            objectSize.applyMatrix4(glassLineRotation);
            mesh.rotation.setFromRotationMatrix(mesh.matrix.clone().multiply(degreeToMatrix(90)));
          } else if (connector.angle === 45) {
            objectLength = new THREE.Vector3(connector.length, 0, connector.width);
            // Angle needs to be sized twice
            objectSize = new THREE.Vector3(0, 0, connector.size / 1000);
            objectSize.add(objectSize.clone().applyMatrix4(glassLineRotation));
            mesh.rotation.setFromRotationMatrix(mesh.matrix.clone().multiply(degreeToMatrix(45)));
          } else {
            objectLength = new THREE.Vector3(connector.length, 0, connector.width);
            objectSize = new THREE.Vector3(0, 0, connector.size / 500);
            objectSize.applyMatrix4(glassLineRotation);
            mesh.rotation.setFromRotationMatrix(mesh.matrix.clone().multiply(degreeToMatrixZ(180)));
          }

          mesh.scale.set(0.002, 0.002, 0.002);

          mesh.castShadow = true;
          mesh.receiveShadow = true;

          objectLength.applyMatrix4(glassLineRotation);

          const positionPoint = objectLength.clone().multiplyScalar(0.5);
          positionPoint.add(origin);
          mesh.position.set(
            positionPoint.x,
            positionPoint.y + (165 / 500),
            positionPoint.z,
          );
          origin.add(objectSize);

          const upperConnector = mesh.clone();
          upperConnector.position.set(
            positionPoint.x,
            positionPoint.y + (35 / 500),
            positionPoint.z,
          );
          glassLineRotation.multiply(degreeToMatrix(connector.angle));

          group.add(mesh);
          group.add(upperConnector);
        } else {
          const geometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
          const material = new THREE.MeshBasicMaterial({ color: '#000000' });
          const generatedObject = new THREE.Mesh(geometry, material);

          generatedObject.matrix.multiply(glassLineRotation);
          generatedObject.rotation.setFromRotationMatrix(generatedObject.matrix);

          const objectLength = new THREE.Vector3(0, 0, connector.width);
          objectLength.applyMatrix4(glassLineRotation);

          generatedObject.position.set(
            origin.x + (objectLength.x / 2),
            dimensions[0].z + 1 + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0),
            origin.z + (objectLength.z / 2),
          );

          glassLineRotation.multiply(degreeToMatrix(connector.angle));
          origin.add(objectLength);
          group.add(generatedObject);
        }
      }

      if (section.clamp2) {
        group.add(renderClamp(
          section.clamp2,
          origin,
          glassLineRotation,
          models,
          dimensions,
          false,
        ));
      }

      if (section.glass) {
        const { glass } = section;
        if (glass.model) {
          // Debug Arrows
          // const helper21 = new THREE.ArrowHelper(
          //   THREE.Vector3(1, 0, 0),
          //   origin.clone(),
          //   1,
          //   0x00a2ff,
          // );
          // group.add(helper21);
          // const helper2 = new THREE.ArrowHelper(
          //   THREE.Vector3(0, 1, 0),
          //   origin.clone(),
          //   1,
          //   0x00a2ff,
          // );
          // group.add(helper2);
          const material = new THREE.MeshLambertMaterial({
            color: 0x87C3FF,
            reflectivity: 1,
            opacity: 0.5,
            transparent: true,
          });
          const mesh = new THREE.Mesh(models[glass.id].clone(), material);

          mesh.matrix = glassLineRotation;
          mesh.rotation.setFromRotationMatrix(mesh.matrix.clone().multiply(degreeToMatrixZ(180)));

          // mesh.rotation.set();
          mesh.scale.set(0.002, 0.002, 0.002);

          mesh.castShadow = true;
          mesh.receiveShadow = true;

          const objectLength = new THREE.Vector3(0.016, 0, glass.width);
          objectLength.applyMatrix4(glassLineRotation);

          const positionPoint = objectLength.clone().multiplyScalar(0.5);
          positionPoint.add(origin);
          mesh.position.set(
            positionPoint.x,
            positionPoint.y,
            positionPoint.z,
          );

          const objectSize = new THREE.Vector3(0, 0, glass.width);
          objectSize.applyMatrix4(glassLineRotation);

          origin.add(objectSize);
          group.add(mesh);
        } else {
          const geometry = new THREE.BoxGeometry(0.1, 2, glass.width);

          const material = new THREE.MeshLambertMaterial({
            color: 0x87C3FF,
            reflectivity: 0.8,
            opacity: 0.6,
            transparent: true,
          });
          const generatedObject = new THREE.Mesh(geometry, material);

          generatedObject.matrix = glassLineRotation;
          generatedObject.rotation.setFromRotationMatrix(generatedObject.matrix);

          const objectLength = new THREE.Vector3(0, 0, glass.width);
          objectLength.applyMatrix4(glassLineRotation);

          const positionPoint = objectLength.clone().multiplyScalar(0.5);
          positionPoint.add(origin);
          generatedObject.position.set(
            positionPoint.x,
            positionPoint.y + 1,
            positionPoint.z,
          );
          origin.add(objectLength);
          group.add(generatedObject);
        }
        // Debug Arrows
        // const helper31 = new THREE.ArrowHelper(
        //   THREE.Vector3(0, 0, 1),
        //   origin.clone(),
        //   1,
        //   0xff00ff,
        // );
        // group.add(helper31);
        // const helper3 = new THREE.ArrowHelper(
        //   THREE.Vector3(0, 1, 0),
        //   origin.clone(),
        //   1,
        //   0xff00ff,
        // );
        // group.add(helper3);
      }
    });
    return group;
  },
};
